import type { CSSProperties } from 'react';

import { AppColors, whiteAlpha } from '@/config/appColors';
import type {
  AppIconName,
  LanguageSwitchIconName,
  SvgIconComponent,
} from '@/config/appIcons';
import { darkenColor } from '@/utils/extensions/colorExtension';
import { wv } from '@/utils/screenUtil';

import { SvgAsset } from './SvgAsset';
import styles from './PrimaryIconButton.module.css';

/**
 * Перенос `lib/src/presentation/widgets/primary_icon_button.dart`.
 *
 * `WidgetStatesController` из Dart-версии сюда не переехал: он только сообщал
 * InkWell о состоянии `selected`, а ни один цвет кнопки от состояний InkWell
 * не зависит — видимой разницы нет.
 */

/** `Colors.white` */
const WHITE = '#ffffff';

/** Умолчания основного конструктора. */
const DEFAULT_ICON_SIZE = 24;
const DEFAULT_SIZE = 70;
const DEFAULT_OPACITY = 1;

/** `AnimatedOpacity(opacity: disabled ? 0.4 : opacity)` */
const DISABLED_OPACITY = 0.4;

/** `BorderRadius.circular(100)` против `BoxShape.circle`. */
const RECTANGLE_BORDER_RADIUS = '100px';
const CIRCLE_BORDER_RADIUS = '50%';

/** `boxShadow = const []` */
const NO_BOX_SHADOW: readonly string[] = [];

export interface PrimaryIconButtonProps {
  /** Имя иконки (`AppIcons.mic` → `'mic'`) или сам SVG-компонент. */
  readonly icon: AppIconName | LanguageSwitchIconName | SvgIconComponent;
  readonly onPressed?: (() => void) | null;
  /** Значения размеров — как в Dart, до умножения на `.w`. */
  readonly iconSize?: number;
  readonly size?: number | null;
  readonly height?: number | null;
  readonly width?: number | null;
  readonly backgroundColor?: string;
  readonly foregroundColor?: string;
  readonly overlayColor?: string;
  /** Каждая строка — одна `BoxShadow` в записи CSS. */
  readonly boxShadow?: readonly string[];
  readonly blur?: number;
  readonly opacity?: number;
  readonly isSelected?: boolean;
  readonly rounded?: boolean;
  readonly className?: string;
}

/** Белый у выбранной, 20% у «капсулы», иначе 50% — порядок как в Dart. */
function resolveBackgroundColor(
  backgroundColor: string | undefined,
  isSelected: boolean,
  rounded: boolean,
): string {
  if (backgroundColor !== undefined) return backgroundColor;
  if (isSelected) return WHITE;
  return rounded ? whiteAlpha(0.2) : whiteAlpha(0.5);
}

/** `widget.size?.w`, `widget.height?.w`, `widget.width?.w`. */
function boxSize(
  primary: number | null | undefined,
  fallback: number | null | undefined,
): string {
  const value = primary ?? fallback;
  return value == null ? 'auto' : wv(value);
}

function buildClipStyle(
  opacity: number,
  disabled: boolean,
  blur: number | undefined,
): CSSProperties {
  return {
    '--opacity': disabled ? DISABLED_OPACITY : opacity,
    ...(blur === undefined ? {} : { '--blur': `${blur}px` }),
  } as CSSProperties;
}

function buildButtonStyle(options: {
  readonly height: number | null | undefined;
  readonly width: number | null | undefined;
  readonly size: number | null | undefined;
  readonly background: string;
  readonly overlay: string;
  readonly isRectangle: boolean;
  readonly boxShadow: readonly string[];
}): CSSProperties {
  return {
    '--box-height': boxSize(options.height, options.size),
    '--box-width': boxSize(options.width, options.size),
    '--background-color': options.background,
    '--overlay-color': options.overlay,
    '--border-radius': options.isRectangle
      ? RECTANGLE_BORDER_RADIUS
      : CIRCLE_BORDER_RADIUS,
    '--box-shadow':
      options.boxShadow.length === 0 ? 'none' : options.boxShadow.join(', '),
  } as CSSProperties;
}

export function PrimaryIconButton({
  icon,
  onPressed,
  iconSize = DEFAULT_ICON_SIZE,
  size = DEFAULT_SIZE,
  height,
  width,
  backgroundColor,
  foregroundColor,
  overlayColor,
  boxShadow = NO_BOX_SHADOW,
  blur,
  opacity = DEFAULT_OPACITY,
  isSelected = false,
  rounded = false,
  className,
}: PrimaryIconButtonProps) {
  const background = resolveBackgroundColor(backgroundColor, isSelected, rounded);
  const foreground =
    foregroundColor ?? (isSelected ? AppColors.c4E4E4E : WHITE);
  const overlay = overlayColor ?? darkenColor(background, 0.1);
  // `height != null && width != null && height != width` — «капсула»,
  // иначе круг.
  const isRectangle = height != null && width != null && height !== width;
  const disabled = onPressed == null;
  const blurred = blur === undefined ? '' : styles.blurred;
  return (
    <div
      className={`${styles.clip} ${blurred} ${className ?? ''}`}
      style={buildClipStyle(opacity, disabled, blur)}
    >
      <button
        type="button"
        className={styles.button}
        style={buildButtonStyle({
          height,
          width,
          size,
          background,
          overlay,
          isRectangle,
          boxShadow,
        })}
        disabled={disabled}
        onClick={() => onPressed?.()}
      >
        <SvgAsset
          icon={icon}
          height={wv(iconSize)}
          color={foreground}
          className={styles.icon}
        />
      </button>
    </div>
  );
}

/** `PrimaryIconButton.rounded`: свечение белым 20% с размытием 10. */
const ROUNDED_BOX_SHADOW: readonly string[] = [`0 0 10px ${whiteAlpha(0.2)}`];
const ROUNDED_HEIGHT = 40;
const ROUNDED_WIDTH = 56;
const ROUNDED_OPACITY = 0.5;
const ROUNDED_BLUR = 10;

/** Именованный конструктор `PrimaryIconButton.rounded` из Dart-версии. */
export type PrimaryIconButtonRoundedProps = Omit<
  PrimaryIconButtonProps,
  'size' | 'rounded' | 'blur' | 'boxShadow'
>;

export function PrimaryIconButtonRounded({
  height = ROUNDED_HEIGHT,
  width = ROUNDED_WIDTH,
  iconSize = DEFAULT_ICON_SIZE,
  opacity = ROUNDED_OPACITY,
  ...rest
}: PrimaryIconButtonRoundedProps) {
  return (
    <PrimaryIconButton
      {...rest}
      height={height}
      width={width}
      iconSize={iconSize}
      opacity={opacity}
      size={null}
      rounded
      blur={ROUNDED_BLUR}
      boxShadow={ROUNDED_BOX_SHADOW}
    />
  );
}
